import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";

// Helper function to parse text with markdown-style formatting
const parseFormattedText = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /(\*\*\[([^\]]+)\]\(([^)]+)\)\*\*)|(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(
        <a key={`boldlink-${keyIndex++}`} href={match[3]} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#166a9c] hover:text-[#166a9c]/80 underline underline-offset-2 transition-colors">
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      parts.push(
        <strong key={`bold-${keyIndex++}`} className="font-semibold text-[#166a9c]">
          {match[5]}
        </strong>
      );
    } else if (match[6]) {
      parts.push(
        <a key={`link-${keyIndex++}`} href={match[8]} target="_blank" rel="noopener noreferrer" className="text-[#166a9c] hover:text-[#166a9c]/80 underline underline-offset-2 transition-colors">
          {match[7]}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
};

const isBulletPoint = (text: string): boolean => {
  return text.trim().startsWith('•') || text.trim().startsWith('-');
};

const renderParagraph = (paragraph: string, pIndex: number) => {
  const lines = paragraph.split('\n');
  const hasBullets = lines.some(line => isBulletPoint(line));
  
  if (hasBullets) {
    return (
      <ul key={pIndex} className="space-y-2 ml-1">
        {lines.map((line, lIndex) => {
          const trimmedLine = line.trim();
          if (isBulletPoint(trimmedLine)) {
            const content = trimmedLine.replace(/^[•\-]\s*/, '');
            return (
              <li key={lIndex} className="flex items-start gap-2">
                <span className="text-[#166a9c] mt-1.5 text-xs">•</span>
                <span className="flex-1">{parseFormattedText(content)}</span>
              </li>
            );
          }
          return <span key={lIndex}>{parseFormattedText(line)}</span>;
        })}
      </ul>
    );
  }
  return <p key={pIndex}>{parseFormattedText(paragraph)}</p>;
};

const DecarbonwizeFAQSection = () => {
  const { t } = useLanguage();
  
  const faqs = [
    { question: t("faq.q1"), answer: t("faq.a1") },
    { question: t("faq.q2"), answer: t("faq.a2") },
    { question: t("faq.q6"), answer: t("faq.a6") },
    { question: t("faq.q3"), answer: t("faq.a3") },
    { question: t("faq.q4"), answer: t("faq.a4") },
    { question: t("faq.q5"), answer: t("faq.a5") }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#166a9c] mb-4">
            {t("faq.title")}
          </h2>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card border border-border/50 rounded-xl px-6 shadow-sm"
              >
                <AccordionTrigger className="text-left text-foreground hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 pb-5 leading-relaxed text-lg">
                  <div className="space-y-4">
                    {faq.answer.split('\n\n').map((paragraph, pIndex) => renderParagraph(paragraph, pIndex))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default DecarbonwizeFAQSection;
